<script setup>
import Layout from "../../layouts/main.vue";
import { onMounted, reactive, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import MethodService from "../../service/MethodService";
import DataForm from "./dataCustomerGroup";
import mushroom from "cem-probe-api";
import Swal from "sweetalert2";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
import { getCurrentPid } from "@/utils/systemType";

const { t } = useI18n();
const loading = ref(false);
const router = useRouter();
const route = useRoute();
const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const dataTables = reactive({ value: [] });
const pageSizeDefault = ref(20);

const showDialogCustomers = ref(false);
const currentGroupName = ref("");
const currentGroupIdNhom = ref(null);
const customerListData = ref([]);
const customerListLoading = ref(false);
const customerListPagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0,
  lengthMenu: [10, 20, 50, 100],
});

const fn_handle = async (type, scope, row) => {
  if (type === "add") {
    router.push({ name: "CustomersGroupCreate" });
  }
  if (type === "view") {
    currentGroupName.value = row.name;
    currentGroupIdNhom.value = row.id_nhom;
    customerListPagination.page = 1;
    showDialogCustomers.value = true;
    await fetchCustomerList();
  }
  if (type === "update") {
    router.push({ name: "CustomersGroupUpdate", params: { id: row.id_nhom } });
  }
  if (type === "delete") {
    Swal.fire({
      title: t("t_warning"),
      text: t("t_record_delete"),
      icon: "warning",
      confirmButtonColor: "#f46a6a",
      confirmButtonText: t("swal_accept"),
      cancelButtonText: t("swal_cancel"),
      showCancelButton: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (row.customerCount > 0) {
          toastr.error(t("t_customer_group_has_customer"));
          return;
        }
        try {
          const response = await mushroom.nhom_khach_hang.deleteAsync(
            {
              id: row.id_nhom,
            },
            {
              extra: {
                headers: {
                  ProbeId: getCurrentPid(),
                },
              },
            },
          );
          if (response.result) {
            toastr.success(t("toastr_delete_success"));
            await fetchCustomersGroupAsync();
          }
        } catch (e) {
          MethodService.showError(e.code);
        }
      }
    });
  }
};

const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
  router
    .replace({
      name: "CustomersGroup",
      query: {
        ...route.query,
        showFormSearch: tableRules.showFormSearch,
      },
    })
    .catch(() => {});
};
const fn_TimKiem = async () => {
  // tableRules.dataSearch.value["name"] = tableRules.dataSearch.value["name"]
  //   ? tableRules.dataSearch.value["name"].trim()
  //   : "";
  // tableRules.filters = MethodService.filterTable(
  //   JSON.stringify(tableRules.dataSearch),
  // );

  const filters = [];
  if (tableRules.dataSearch.value["name"]) {
    filters.push(`name=${tableRules.dataSearch.value["name"]}`);
  }
  tableRules.filters = filters.join(",");
  tableRules.offset = 0;
  tableRules.sort = "";
  await fetchCustomersGroupAsync();
};

const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  pageSizeDefault.value = limit;
  fn_tableChangeOffset(tableRules.page);
};
const fn_tableCurentChange = (page) => {
  fn_tableChangeOffset(page);
};
const fn_tablePrevClick = (page) => {
  fn_tableChangeOffset(page - 1);
};
const fn_tableNextClick = (page) => {
  fn_tableChangeOffset(page + 1);
};
const fn_tableChangeOffset = async (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * pageSizeDefault.value;
  await fetchCustomersGroupAsync();
};
const fn_tableSortChange = async (column) => {
  MethodService.tableSortChange(column, tableRules);
  loading.value = true;
  await fetchCustomersGroupAsync();
};
const fn_hashUrl = () => {
  MethodService.hashUrl(route.query, tableRules);
  pageSizeDefault.value = tableRules.limit;
};

const changeData = async (data) => {
  const promises = data.map(async (item) => {
    if (!item.id_nhom) return { ...item, customerCount: 0 };
    try {
      const res = await mushroom.nhom_khach_hang.getCustomersInGroupAsync({
        body: {
          id_nhom: item.id_nhom,
          // _limit: 1,
          // _offset: 0,
          probe_id: getCurrentPid(),
        },
      });
      return { ...item, customerCount: res.result?.meta?.total ?? 0 };
    } catch {
      return { ...item, customerCount: 0 };
    }
  });
  return Promise.all(promises);
};

const fetchCustomersGroupAsync = async () => {
  loading.value = true;
  const dataFilter = {
    filters: tableRules.filters,
    fields: tableRules.fields,
    sort: tableRules.sort,
  };
  router
    .replace({
      name: "CustomersGroup",
      query: {
        limit: tableRules.limit,
        page: tableRules.page,
        offset: tableRules.offset,
        sort: tableRules.sort,
        filters: tableRules.filters,
        showFormSearch: tableRules.showFormSearch,
      },
    })
    .catch(() => {});

  try {
    const response = await mushroom.nhom_khach_hang.listAsync(dataFilter, {
      enabledCache: false,
      extra: {
        headers: {
          ProbeId: getCurrentPid(),
        },
        params: {
          _limit: tableRules.limit,
          _offset: tableRules.offset,
        },
      },
    });
    if (response.result) {
      dataTables.value = await changeData(response.result);
      tableRules.total = response.meta.total;
      // tableRules.page = response.meta.offset / response.meta.limit + 1;
      tableRules.page = response.meta.offset / pageSizeDefault.value + 1;
    }
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

const fetchCustomerList = async () => {
  customerListLoading.value = true;
  try {
    const res = await mushroom.nhom_khach_hang.getCustomersInGroupAsync({
      body: {
        id_nhom: currentGroupIdNhom.value,
        _limit: customerListPagination.pageSize,
        _offset:
          (customerListPagination.page - 1) * customerListPagination.pageSize,
        probe_id: getCurrentPid(),
      },
    });
    customerListData.value = res.result?.data ?? [];
    customerListPagination.total = res.result?.meta?.total ?? 0;
  } catch (e) {
    console.error("Có lỗi: %o", e);
    customerListData.value = [];
    customerListPagination.total = 0;
  } finally {
    customerListLoading.value = false;
  }
};
const fn_customerPageChange = async (page) => {
  customerListPagination.page = page;
  await fetchCustomerList();
};
const fn_customerSizeChange = async (pageSize) => {
  customerListPagination.pageSize = pageSize;
  customerListPagination.page = 1;
  await fetchCustomerList();
};

onMounted(async () => {
  fn_hashUrl();
  await fetchCustomersGroupAsync();
});
</script>

<template>
  <Layout>
    <div class="card" style="min-height: 80vh">
      <div class="card-header">
        <h3 class="text-uppercase mb-0">{{ $t("t_list_customer_group") }}</h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button
              class="btn btn-secondary btn-border"
              @click="fn_handle('add', null)"
            >
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>
      <div class="card-body" style="padding: 0 0 15px 0">
        <div class="main-body">
          <div class="main-body-search mx-3 mb-3">
            <el-collapse-transition>
              <div v-show="tableRules.showFormSearch">
                <b-card>
                  <el-form
                    label-position="top"
                    @submit.prevent
                    :model="tableRules.dataSearch.value"
                  >
                    <b-row class="align-items-end">
                      <b-col md="8">
                        <el-form-item
                          :label="$t('t_customer_group')"
                          class="mb-0"
                        >
                          <el-input
                            clearable
                            v-model="tableRules.dataSearch.value['name']"
                            :placeholder="$t('t_customer_group')"
                            @keyup.enter="fn_TimKiem()"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="4" class="text-end">
                        <button
                          type="button"
                          class="btn btn-soft-secondary btn-border me-2"
                          style="width: 100px"
                          @click="fn_showFormTimKiem()"
                        >
                          {{ $t("t-hide") }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                          style="width: 100px"
                          @click="fn_TimKiem()"
                        >
                          {{ $t("t-search") }}
                        </button>
                      </b-col>
                    </b-row>
                  </el-form>
                </b-card>
              </div>
            </el-collapse-transition>
          </div>
          <div class="mb-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
          <el-table
            size="small"
            :data="dataTables.value"
            style="width: 100%"
            max-height="550"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column :label="$t('t-stt')" width="70" align="center">
              <template #default="scope">
                {{ tableRules.offset + scope.$index + 1 }}
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              :label="$t('t_customer_group')"
              min-width="150"
              align="center"
              sortable="custom"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.name }}</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="customerCount"
              :label="$t('t_customer_number')"
              min-width="150"
              align="center"
            />
            <el-table-column
              :label="$t('t_description')"
              min-width="200"
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.description }}</div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('t-action')"
              align="center"
              width="150px"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    :content="$t('t-view')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-info waves-effect waves-light btn-sm"
                      @click="fn_handle('view', scope.$index, scope.row)"
                    >
                      <i class="ri-eye-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('t-edit')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-success waves-effect waves-light btn-sm"
                      @click="fn_handle('update', scope.$index, scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('t-delete')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      @click="fn_handle('delete', scope.$index, scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div class="mt-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog danh sách khách hàng trong nhóm -->
    <el-dialog
      v-model="showDialogCustomers"
      :title="$t('t_customer_list_in_group') + ' ' + currentGroupName"
      width="80%"
    >
      <el-table
        :data="customerListData"
        size="small"
        style="width: 100%"
        v-loading="customerListLoading"
        :empty-text="$t('t-no-data')"
        border
        stripe
      >
        <el-table-column :label="$t('t-stt')" width="70" align="center">
          <template #default="scope">
            {{
              (customerListPagination.page - 1) *
                customerListPagination.pageSize +
              scope.$index +
              1
            }}
          </template>
        </el-table-column>
        <el-table-column label="VLAN" width="80" align="center">
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["vlan_id_outer"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          label="MAC"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["client_mac"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          label="Account"
          min-width="120"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["account"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_profile_name')"
          min-width="130"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["profile_name"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_profile_speed')"
          width="120"
          align="center"
        >
          <template #default="scope">
            {{ scope.row["profile_speed"] }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_olt_code')"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["olt_code"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_bras_code')"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">{{ scope.row["bras_code"] }}</div>
          </template>
        </el-table-column>
      </el-table>
      <div class="mt-3" style="display: flex; justify-content: flex-end">
        <el-pagination
          small
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="customerListPagination.total"
          :page-sizes="customerListPagination.lengthMenu"
          :page-size="customerListPagination.pageSize"
          :current-page="customerListPagination.page"
          @current-change="fn_customerPageChange"
          @size-change="fn_customerSizeChange"
        />
      </div>
    </el-dialog>
  </Layout>
</template>
